/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * AI SDK v5 Compatibility Utilities
 * Provides functions to normalize and convert messages between v4 and v5 formats
 */

export interface MessageV5 {
  id: string
  role: 'user' | 'assistant' | 'system'
  content?: string
  parts?: Array<{
    type: 'text' | 'image' | 'file' | 'tool-invocation' | 'tool-result'
    text?: string
    url?: string
    contentType?: string
    name?: string
    toolCallId?: string
    toolName?: string
    args?: any
    result?: any
    output?: any
    state?: string
    [key: string]: any
  }>
  [key: string]: any
}

export interface MessageV4 {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  toolInvocations?: any[]
  [key: string]: any
}

/**
 * Detects if a message is in v4 format
 */
export function isMessageV4(message: any): boolean {
  return !message.parts && (message.content !== undefined || message.toolInvocations !== undefined)
}

/**
 * Converts a v4 message to v5 format
 */
export function migrateMessageV4ToV5(messageV4: MessageV4): MessageV5 {
  const messageV5: MessageV5 = {
    id: messageV4.id,
    role: messageV4.role,
    parts: []
  }

  // Copy other properties
  Object.keys(messageV4).forEach(key => {
    if (key !== 'content' && key !== 'id' && key !== 'role' && key !== 'toolInvocations') {
      messageV5[key] = messageV4[key]
    }
  })

  // Convert content to parts
  if (messageV4.content) {
    if (Array.isArray(messageV4.content)) {
      // Handle CoreMessage format where content is an array of parts
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      messageV4.content.forEach((part: any) => {
        if (part.type === 'text') {
          messageV5.parts!.push({
            type: 'text',
            text: part.text
          })
        } else if (part.type === 'tool-call') {
          messageV5.parts!.push({
            type: 'tool-invocation',
            toolCallId: part.toolCallId,
            toolName: part.toolName,
            args: part.args,
            state: 'call'
          })
        } else if (part.type === 'image') {
          messageV5.parts!.push({
            type: 'image',
            image: part.image,
            mimeType: part.mimeType
          })
        } else if (part.type === 'tool-result') {
          messageV5.parts!.push({
            type: 'tool-result',
            toolCallId: part.toolCallId,
            toolName: part.toolName,
            result: part.result,
            output: part.result
          })
        }
      })
    } else {
      // Handle simple string content
      messageV5.parts!.push({
        type: 'text',
        text: messageV4.content
      })
    }
  }

  // Convert toolInvocations to parts
  if (messageV4.toolInvocations && messageV4.toolInvocations.length > 0) {
    messageV4.toolInvocations.forEach(invocation => {
      messageV5.parts!.push({
        type: 'tool-invocation',
        toolCallId: invocation.toolCallId,
        toolName: invocation.toolName,
        args: invocation.args,
        result: invocation.result,
        state: invocation.state
      })
    })
  }

  return messageV5
}

/**
 * Normalizes a message to v5 format
 */
export function normalizeMessageToV5(message: any): MessageV5 {
  if (isMessageV4(message)) {
    return migrateMessageV4ToV5(message)
  }
  return message as MessageV5
}

/**
 * Normalizes an array of messages to v5 format
 */
export function normalizeMessagesToV5(messages: any[]): MessageV5[] {
  return messages.map(normalizeMessageToV5)
}

/**
 * Extracts text content from a v5 message
 */
export function extractTextFromMessageV5(message: MessageV5): string {
  if (!message.parts) return message.content || ''

  return message.parts
    .filter(part => part.type === 'text')
    .map(part => part.text || '')
    .join('')
}

/**
 * Extracts message content (works with both v4 and v5)
 */
export function extractMessageContent(message: any): string {
  // Guard against null/undefined message
  if (!message) {
    return ''
  }

  // v5 format with parts
  if (message.parts && Array.isArray(message.parts)) {
    return message.parts
      .filter((part: any) => part.type === 'text')
      .map((part: any) => part.text || '')
      .join('')
  }

  // v4 format with content
  if (message.content !== undefined) {
    return message.content
  }

  return ''
}

/**
 * Extracts attachments from a v5 message
 */
export function extractAttachmentsFromMessageV5(message: MessageV5): any[] {
  if (!message.parts) return []

  return message.parts
    .filter(part => part.type === 'image' || part.type === 'file')
    .map(part => ({
      name: part.name,
      url: part.url,
      contentType: part.contentType
    }))
}
